<template>
  <teleport :to="teleportTo" :disabled="!teleportTo">
    <div
      ref="modal"
      :class="['border-gray-20 absolute z-[1000] overflow-hidden rounded-lg border bg-white shadow-md', $attrs.class]"
      v-bind="{ ...$attrs, class: undefined }"
      :style="draggableStyle"
      :data-testid="props.testId"
    >
      <div
        ref="header"
        class="border-gray-20 flex select-none items-center justify-between gap-2 border-b bg-white p-5"
        :class="isDragging ? 'cursor-grabbing' : 'cursor-grab'"
      >
        <div class="flex items-center gap-2">
          <slot name="icon">
            <move-icon class="icon-m" />
          </slot>

          <slot name="header">
            <span class="text-gray-90 font-medium">{{ title }}</span>
          </slot>
        </div>

        <ui-close-button small @click="$emit('close')" />
      </div>

      <div class="overflow-auto" :style="{ height: `calc(100% - 65px)` }">
        <slot />
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, computed, type MaybeRef, unref } from 'vue'
import { useDraggable } from '@vueuse/core'
import { UiCloseButton } from '@klausapp/ui-kit'

import MoveIcon from './assets/move.svg'

interface Props {
  title?: string
  initialX?: number
  initialY?: number
  containerRef?: MaybeRef<HTMLElement | undefined>
  teleportTo?: string | HTMLElement
  positionStorageKey?: string
  testId?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  initialX: 0,
  initialY: 0,
  containerRef: undefined,
  teleportTo: undefined,
  positionStorageKey: undefined,
  testId: undefined,
})

defineEmits<{
  (e: 'close'): void
}>()

defineOptions({
  inheritAttrs: false,
})

const modal = ref<HTMLElement>()
const header = ref<HTMLElement>()

const containerElement = computed(() => {
  return unref(props.containerRef) || document.documentElement
})

// Load saved position from localStorage
const getSavedPosition = () => {
  if (!props.positionStorageKey) return null
  try {
    const saved = localStorage.getItem(`floating-modal-position-${props.positionStorageKey}`)
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

// Validate if a position is within bounds
const isPositionValid = (position: { x: number; y: number }) => {
  const container = containerElement.value
  const containerWidth = container === document.documentElement ? window.innerWidth : container.clientWidth
  const containerHeight = container === document.documentElement ? window.innerHeight : container.clientHeight

  // Check if position is negative or if modal would be mostly off-screen
  // Allow up to 100px off-screen to account for dragging near edges
  const minVisiblePx = 100
  return (
    position.x >= -minVisiblePx &&
    position.y >= -minVisiblePx &&
    position.x < containerWidth &&
    position.y < containerHeight
  )
}

// Calculate centered position (uses estimated modal size)
const getCenteredPosition = () => {
  const container = containerElement.value
  const containerWidth = container === document.documentElement ? window.innerWidth : container.clientWidth
  const containerHeight = container === document.documentElement ? window.innerHeight : container.clientHeight

  // Use estimated modal dimensions (320x700 is typical for evaluation form)
  const estimatedWidth = 320
  const estimatedHeight = 700

  return {
    x: Math.max(0, (containerWidth - estimatedWidth) / 2),
    y: Math.max(0, (containerHeight - estimatedHeight) / 2),
  }
}

// Adjust initial position based on whether we have a container
const adjustedInitialPosition = computed(() => {
  const container = containerElement.value

  // First, try to get saved position from localStorage
  const savedPosition = getSavedPosition()
  if (savedPosition) {
    // Validate if saved position is still valid for current screen size
    if (isPositionValid(savedPosition)) {
      return { x: savedPosition.x, y: savedPosition.y }
    } else {
      // Position is out of bounds, clear it and use centered position
      if (props.positionStorageKey) {
        try {
          localStorage.removeItem(`floating-modal-position-${props.positionStorageKey}`)
        } catch {
          // Ignore storage errors
        }
      }
      return getCenteredPosition()
    }
  }

  // Fall back to provided initial position
  // If using document.documentElement, use coordinates as-is
  if (container === document.documentElement) {
    return { x: props.initialX, y: props.initialY }
  }

  // If using a specific container, coordinates should be relative to that container
  // So we don't need to adjust them - useDraggable will handle the positioning
  return { x: props.initialX, y: props.initialY }
})

const { style: draggableStyle, isDragging } = useDraggable(modal, {
  initialValue: adjustedInitialPosition,
  handle: header,
  containerElement,
  onEnd: (position) => {
    if (props.positionStorageKey) {
      try {
        localStorage.setItem(
          `floating-modal-position-${props.positionStorageKey}`,
          JSON.stringify({ x: position.x, y: position.y })
        )
      } catch {
        // Ignore storage errors (incognito mode, storage full, etc.)
      }
    }
  },
})
</script>
